/**
 * /post-checkout
 *
 * Universal landing page after a Stripe Checkout success URL. Handles three
 * flows for a class enrolment payment:
 *
 *   1. The visitor is already logged in
 *      → "You're enrolled, head to your portal" + Portal CTA.
 *
 *   2. Guest paid AND their email is on an existing account
 *      → "Welcome back! Sign in to see your booking." Single Log-in CTA
 *        with the email pre-filled, and a quiet "create new account" link
 *        as a fallback.
 *
 *   3. Guest paid with a brand-new email
 *      → Two equally weighted buttons: "Create an account" (one-click — name,
 *        email and phone are pre-filled) AND "Log in with another email".
 *
 * Payment is collected BEFORE this page. The booking is parked in the
 * `pending_class_enrolments` collection and claimed automatically when the
 * visitor finishes signing in or registering (see /auth/login + /register
 * auto-claim hooks).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { CheckCircle, ArrowRight, SignIn, UserPlus, EnvelopeSimple, Spinner, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PostCheckout() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { user, login: setAuth } = useAuth();
  const [polling, setPolling] = useState(true);
  const [info, setInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState("");

  // Poll the backend for the post-checkout info until the payment is recorded
  // as paid. We hit the status endpoint EVERY tick (it's now public/optional-
  // auth) so we don't have to wait for Stripe's webhook to land. This is the
  // safety net that prevents "I paid but my booking didn't appear" — even if
  // the webhook is misconfigured or delayed, polling will flip the status
  // and fire the side-effect handler that creates the enrolment row.
  //
  // We keep polling in the BACKGROUND even after rendering the action
  // buttons. That way the visitor can click "Create account" / "Log in"
  // immediately, and the booking will auto-claim once the webhook lands.
  useEffect(() => {
    if (!sessionId) {
      setError("Missing session — payment can't be verified.");
      setPolling(false);
      return;
    }
    let cancelled = false;
    let timer;
    const tick = async (n = 0) => {
      if (cancelled) return;
      try {
        try { await api.get(`/payments/checkout/status/${sessionId}`); }
        catch (e) { /* tolerate transient errors — we'll retry */ }
        const r = await api.get(`/payments/post-checkout-info/${sessionId}`);
        if (cancelled) return;
        setInfo(r.data);
        if (r.data.payment_status === "paid") {
          setPolling(false);
          return;
        }
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 404 && n > 4) {
          setError("Payment record not found. If you paid, please check your email — the team will be in touch.");
          setPolling(false);
          return;
        }
      }
      setAttempts(n + 1);
      // Poll for up to ~3 minutes total — Stripe webhooks usually arrive
      // within 30s but occasionally take longer in test/sandbox modes.
      if (n > 90) {
        setPolling(false);
        return;
      }
      timer = setTimeout(() => tick(n + 1), 2000);
    };
    tick(0);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // When an already-logged-in user lands here, claim the enrolment if a
  // pending row exists (covers the edge case of a guest who happened to be
  // logged into Stripe but not into the site). Then route them onward.
  useEffect(() => {
    if (!user || !info || info.payment_status !== "paid") return;
    let cancelled = false;
    (async () => {
      try { await api.post(`/payments/claim-pending/${sessionId}`); }
      catch {}
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [user, info, sessionId]);

  const classLabel = useMemo(() => {
    const c = info?.class;
    if (!c) return "";
    let s = c.title || "Your class";
    if (c.starts_at) {
      try {
        s += " — " + new Date(c.starts_at).toLocaleDateString("en-AU", {
          weekday: "short", day: "numeric", month: "short", year: "numeric",
          timeZone: "Australia/Brisbane",
        });
      } catch {}
    }
    if (c.location) s += " · " + c.location;
    return s;
  }, [info]);

  // -------- Render branches --------

  if (!sessionId) {
    return <ShellMessage title="Hmm, no payment to confirm" body="This page is meant for the moment after a Stripe checkout. Head back home." />;
  }

  if (polling && !info) {
    return (
      <Shell>
        <div className="text-center py-16" data-testid="post-checkout-polling">
          <Spinner size={48} weight="duotone" className="mx-auto text-[#1b4a76] animate-spin mb-4" />
          <h1 className="font-heading text-2xl font-bold text-stone-900 mb-1">Confirming your payment…</h1>
          <p className="text-stone-600">This usually takes a few seconds.</p>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="text-center py-16" data-testid="post-checkout-error">
          <Warning size={48} weight="duotone" className="mx-auto text-amber-600 mb-4" />
          <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">{error}</h1>
          <Link to="/" className="btn-secondary inline-flex items-center gap-1.5">Back to home <ArrowRight size={16} /></Link>
        </div>
      </Shell>
    );
  }

  // We deliberately do NOT block the page on payment_status !== "paid".
  // Stripe's session.payment_status occasionally lags by 30-60s after the
  // visitor completes the form, and the webhook can be delayed further on
  // production. By rendering the create-account / log-in CTAs immediately,
  // the visitor can finish their journey while the booking lands in the
  // background — auto-claim picks it up the moment they finish signing in.
  const isConfirmed = info?.payment_status === "paid";

  // Render the action page even before we've heard "paid" from Stripe — we
  // trust the visitor completed the Stripe form (they wouldn't be on this URL
  // otherwise) and we have a tx record. The booking auto-attaches once the
  // webhook arrives.

  // Already-signed-in path
  if (user) {
    return (
      <Shell>
        <PaidBanner classLabel={classLabel} isConfirmed={isConfirmed} />
        <div className="bg-white rounded-2xl border border-stone-200 p-8 mt-6 text-center" data-testid="post-checkout-already-loggedin">
          <h2 className="font-heading text-xl font-bold text-stone-900 mb-2">You're all set, {user.first_name || user.name?.split(" ")[0] || "there"}!</h2>
          <p className="text-stone-600 mb-6">Your booking will be in your portal{isConfirmed ? "" : " as soon as Stripe finishes confirming"}. If it isn't visible yet, the <strong>"I paid but my booking isn't here"</strong> link on the bookings page will pull it through.</p>
          <button onClick={() => navigate("/portal/bookings")} className="btn-primary inline-flex items-center gap-2" data-testid="post-checkout-go-portal">
            Go to my portal <ArrowRight size={16} />
          </button>
        </div>
      </Shell>
    );
  }

  // Guest path — info may still be null if the post-checkout-info endpoint
  // failed entirely (e.g. transient network issue). Fall back to a minimal
  // two-button page using sensible defaults so the visitor isn't stuck.
  const safeInfo = info || { email: "", name: "", phone: "", email_exists: false };
  const emailExists = !!safeInfo.email_exists;
  const email = safeInfo.email || "";
  const name = safeInfo.name || "";
  const phone = safeInfo.phone || "";
  const buildLoginHref = () => `/login?session_id=${encodeURIComponent(sessionId)}${email ? `&email=${encodeURIComponent(email)}` : ""}`;
  const buildRegisterHref = () => {
    const qs = new URLSearchParams({ session_id: sessionId });
    if (email) qs.set("email", email);
    if (name) qs.set("name", name);
    if (phone) qs.set("phone", phone);
    return `/register?${qs.toString()}`;
  };

  return (
    <Shell>
      <PaidBanner classLabel={classLabel} isConfirmed={isConfirmed} />
      <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-10 mt-6 text-center" data-testid="post-checkout-guest">
        {emailExists ? (
          <>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900 mb-2" data-testid="post-checkout-welcome-back">Welcome back!</h2>
            <p className="text-stone-600 mb-2">It looks like <strong className="text-stone-900">{email}</strong> already has an account with us.</p>
            <p className="text-stone-600 mb-8">Sign in and your new booking will be waiting in your portal.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
              <Link
                to={buildLoginHref()}
                data-testid="post-checkout-login-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1b4a76] hover:bg-[#143a5e] text-white rounded-xl px-6 py-4 font-semibold text-base shadow-sm transition-colors"
              >
                <SignIn size={20} weight="duotone" /> Log in to my account
              </Link>
            </div>
            <p className="text-xs text-stone-500 mt-5">
              Not your account? <Link to={buildRegisterHref()} className="underline text-[#1b4a76]" data-testid="post-checkout-register-fallback">Create a new one instead</Link>.
            </p>
          </>
        ) : (
          <>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900 mb-2" data-testid="post-checkout-new-customer">One last step — choose how to access your booking</h2>
            <p className="text-stone-600 mb-8 max-w-xl mx-auto">
              {isConfirmed
                ? "Your payment is confirmed. Set up access so you can see your booking, message the team, and unlock the included online course."
                : "Stripe is finalising your payment in the background. While that wraps up, set up access so your booking lands in your portal automatically."}
            </p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto" data-testid="post-checkout-two-buttons">
              <BigButton
                to={buildRegisterHref()}
                colour="brand"
                icon={UserPlus}
                title="Create a new account"
                subtitle="One-click — your details are pre-filled. Save your bookings, track training & get reports."
                testId="post-checkout-register-btn"
              />
              <BigButton
                to={buildLoginHref()}
                colour="navy"
                icon={SignIn}
                title="I already have an account"
                subtitle="Log in and we'll attach this booking automatically."
                testId="post-checkout-login-btn"
              />
            </div>
            <SetByEmail email={email} sessionId={sessionId} onSent={() => {}} />
          </>
        )}
      </div>
    </Shell>
  );
}

// ---------- helpers ----------

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {children}
      </main>
      <PublicFooter />
    </div>
  );
}

function ShellMessage({ title, body }) {
  return (
    <Shell>
      <div className="text-center py-16">
        <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">{title}</h1>
        <p className="text-stone-600 mb-6">{body}</p>
        <Link to="/" className="btn-primary inline-flex items-center gap-1.5">Back to home <ArrowRight size={16} /></Link>
      </div>
    </Shell>
  );
}

function PaidBanner({ classLabel, isConfirmed = true }) {
  if (isConfirmed) {
    return (
      <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 flex items-start gap-4" data-testid="post-checkout-paid-banner">
        <CheckCircle size={32} weight="duotone" className="text-emerald-700 flex-shrink-0" />
        <div>
          <h1 className="font-heading font-bold text-emerald-900 text-xl">Payment complete — you're enrolled! 🎉</h1>
          {classLabel && <p className="text-sm text-emerald-800 mt-1" data-testid="post-checkout-class-label">{classLabel}</p>}
        </div>
      </div>
    );
  }
  // Still-confirming variant — friendly amber spinner, never a dead-end.
  return (
    <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-5 flex items-start gap-4" data-testid="post-checkout-confirming-banner">
      <Spinner size={32} weight="duotone" className="text-amber-700 flex-shrink-0 animate-spin" />
      <div>
        <h1 className="font-heading font-bold text-amber-900 text-xl">Thanks — Stripe is wrapping up your payment</h1>
        <p className="text-sm text-amber-800 mt-1">This usually takes 10–30 seconds. Set up access below and your booking will appear in your portal automatically.</p>
        {classLabel && <p className="text-sm text-amber-800 mt-1" data-testid="post-checkout-class-label">{classLabel}</p>}
      </div>
    </div>
  );
}

function BigButton({ to, colour, icon: Icon, title, subtitle, testId }) {
  const palette = {
    brand: "bg-[#f38838] hover:bg-[#e07523] text-white border-transparent",
    navy: "bg-white hover:bg-stone-50 text-[#1b4a76] border-[#1b4a76]",
  }[colour] || "bg-white hover:bg-stone-50 text-stone-900 border-stone-300";
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`flex flex-col items-start gap-2 p-6 rounded-2xl border-2 ${palette} shadow-sm transition-all hover:-translate-y-0.5 text-left`}
    >
      <Icon size={28} weight="duotone" />
      <div>
        <div className="font-heading font-bold text-lg">{title}</div>
        <div className={`text-sm mt-1 ${colour === "brand" ? "text-white/90" : "text-stone-600"}`}>{subtitle}</div>
      </div>
    </Link>
  );
}

function SetByEmail({ email, sessionId, onSent }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const trigger = async () => {
    setSending(true);
    try {
      await api.post("/auth/magic-set-password", { email, session_id: sessionId });
      setSent(true);
      toast.success("Check your email for the set-password link");
      onSent && onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the link — please use the buttons above.");
    } finally {
      setSending(false);
    }
  };
  if (sent) {
    return (
      <div className="mt-6 text-sm text-emerald-700 inline-flex items-center gap-1.5" data-testid="post-checkout-magic-sent">
        <CheckCircle size={16} weight="fill" /> We've emailed <strong>{email}</strong> with a link to set your password.
      </div>
    );
  }
  return (
    <div className="mt-6 text-xs text-stone-500" data-testid="post-checkout-magic">
      Prefer not to choose a password right now?{" "}
      <button
        type="button"
        onClick={trigger}
        disabled={sending}
        data-testid="post-checkout-magic-btn"
        className="inline-flex items-center gap-1 underline hover:text-stone-900 disabled:opacity-60"
      >
        <EnvelopeSimple size={12} weight="bold" /> {sending ? "Sending…" : "Email me a set-password link"}
      </button>
    </div>
  );
}
